import { Music, Check, X, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useGenres, useUpdateGenre, useDeleteGenre } from "@/hooks/useAdminData";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Genres() {
  const { data: genres, isLoading } = useGenres();
  const updateGenre = useUpdateGenre();
  const deleteGenre = useDeleteGenre();

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await updateGenre.mutateAsync({ id, is_active: isActive });
      toast.success(isActive ? "Жанр активирован" : "Жанр деактивирован");
    } catch (error) {
      toast.error("Ошибка при обновлении");
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await deleteGenre.mutateAsync(id);
      toast.success(`Жанр "${name}" удалён`);
    } catch (error) {
      toast.error("Ошибка при удалении. Возможно, жанр используется в генерациях.");
    }
  };

  return (
    <AdminLayout title="Жанры" description="Управление музыкальными жанрами">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="w-5 h-5 text-primary" />
            Список жанров
          </CardTitle>
          <CardDescription>Включите или выключите жанры</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {genres?.map((genre) => (
                <div key={genre.id} className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border/50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-xl">
                      {genre.emoji}
                    </div>
                    <div>
                      <p className="font-medium">{genre.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Suno стиль: {genre.suno_style || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {genre.is_active ? (
                      <span className="text-xs text-primary flex items-center gap-1">
                        <Check className="w-3 h-3" /> Активно
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <X className="w-3 h-3" /> Неактивно
                      </span>
                    )}
                    <Switch
                      checked={genre.is_active}
                      onCheckedChange={(checked) => handleToggle(genre.id, checked)}
                    />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Удалить жанр «{genre.name}»?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Это действие нельзя отменить. Если жанр используется в генерациях, удаление может не сработать.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Отмена</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => handleDelete(genre.id, genre.name)}
                          >
                            Удалить
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
